import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  BookOpen, 
  Flame, 
  ScrollText, 
  GraduationCap, 
  Sparkles,
  Sun,
  Moon,
  ChevronRight,
  BookMarked,
  Users,
  Heart
} from 'lucide-react';

const features = [
  {
    icon: BookOpen,
    title: 'Estudio Profundo',
    description: 'Análisis versículo por versículo con contexto histórico, lingüístico y doctrinal.'
  },
  {
    icon: GraduationCap,
    title: 'Modo Seminario',
    description: 'Genera material didáctico, preguntas y dinámicas para tus clases.'
  },
  {
    icon: ScrollText,
    title: 'Profundidad Doctrinal',
    description: 'Explora temas con análisis comparativo y referencias a discursos oficiales.'
  },
  {
    icon: Sparkles,
    title: 'Conexión Espiritual',
    description: 'Reflexiones guiadas con escrituras relacionadas y preguntas introspectivas.'
  },
  {
    icon: BookMarked,
    title: 'Repositorio Personal',
    description: 'Guarda notas, crea bosquejos y exporta en PDF tu estudio.'
  },
  {
    icon: Users,
    title: 'Para Todos',
    description: 'Diseñado para maestros, jóvenes, líderes y familias.'
  }
];

const LandingPage = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur">
        <div className="container flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Flame className="h-8 w-8 text-primary" />
            <span className="font-heading text-xl font-bold">Escrituras SUD</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              data-testid="landing-theme-toggle"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
            {user ? (
              <Link to="/dashboard">
                <Button data-testid="go-to-dashboard-btn">
                  Ir al Panel
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            ) : (
              <Link to="/auth">
                <Button data-testid="get-started-btn">
                  Comenzar
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.1),transparent_50%)]" />
        <div className="container px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Heart className="h-4 w-4" />
              Para La Iglesia de Jesucristo de los Santos de los Últimos Días
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Profundiza tu Estudio de las{' '}
              <span className="text-primary">Escrituras</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Una herramienta de estudio con inteligencia artificial para la Biblia, 
              el Libro de Mormón, Doctrina y Convenios, y la Perla de Gran Precio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth">
                <Button size="lg" className="gap-2 text-base" data-testid="hero-cta-btn">
                  <BookOpen className="h-5 w-5" />
                  Comenzar a Estudiar
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="gap-2 text-base" asChild>
                <a href="#features">
                  Conocer Más
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Scripture Volumes */}
      <section className="py-16 bg-muted/30">
        <div className="container px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
              Las Escrituras Restauradas
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Accede a los cuatro volúmenes canónicos con análisis profundo
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { name: 'La Biblia', icon: '📖', id: 'bible' },
              { name: 'Libro de Mormón', icon: '📜', id: 'bom' },
              { name: 'Doctrina y Convenios', icon: '📚', id: 'dc' },
              { name: 'Perla de Gran Precio', icon: '💎', id: 'pgp' },
            ].map((vol) => (
              <Card key={vol.id} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <span className="text-4xl mb-3 block">{vol.icon}</span>
                  <h3 className="font-heading font-semibold text-sm sm:text-base">{vol.name}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20">
        <div className="container px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
              Herramientas de Estudio
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Todo lo que necesitas para un estudio profundo y significativo
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {features.map((feature) => (
              <Card key={feature.title} className="group hover:shadow-lg transition-all hover:-translate-y-1">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary/5">
        <div className="container px-4">
          <div className="max-w-2xl mx-auto text-center">
            <Flame className="h-12 w-12 text-primary mx-auto mb-6" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
              Comienza tu Viaje Espiritual
            </h2>
            <p className="text-muted-foreground mb-8">
              Únete a miles de estudiantes que profundizan su conocimiento de las escrituras cada día.
            </p>
            <Link to="/auth">
              <Button size="lg" className="gap-2" data-testid="cta-register-btn">
                Crear Cuenta Gratuita
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="container px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-primary" />
              <span className="font-heading text-sm">Escrituras SUD</span>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Una herramienta de estudio para los Santos de los Últimos Días
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
